import { Component, inject, OnInit, signal } from '@angular/core';
import { HOME_ROUTE, WALLET_BNPL_DETAIL_ROUTE } from '../../data-access/constants/app-routes';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { IWalletActionButton } from './models/wallet-action-button.interface';
import { WalletActionsComponent } from './components/wallet-actions/wallet-actions.component';
import { InventoryAvailibleComponent } from './components/inventory-availible/inventory-availible.component';
import { WalletDipositHintsComponent } from './components/wallet-diposit-hints/wallet-diposit-hints.component';
import { WalletService } from './services/wallet.service';
import { WalletCreditComponent } from './components/wallet-credit/wallet-credit.component';
import { ActivatedRoute, Router } from '@angular/router';
import { IWallet } from './models/wallet.interface';
import { IProcessData } from './models/wallet-process.interface';
import { RouteStateService } from '@client-monorepo/common/utilities';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { WalletBnplBottomsheetComponent } from './components/wallet-bnpl-bottomsheet/wallet-bnpl-bottomsheet.component';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { WALLET_BNPL_PROCESS_API, WALLET_DEPOSIT_PROCESS_API, WALLET_WITHDROW_PROCESS_API } from '../../data-access/constants/api';
import { IWalletProcess, ProcessActionType } from './models/wallet-cashin-model.interface';
import { ErrorCodes } from '../../data-access/enums/error-codes';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { WalletBnplCancelBottomsheetComponent } from './components/wallet-bnpl-cancel-bottomsheet/wallet-bnpl-cancel-bottomsheet.component';
import { WalletBnplLessAmountBottomsheetComponent } from './components/wallet-bnpl-less-amount-bottomsheet/wallet-bnpl-less-amount-bottomsheet.component';
import { ActionHandlerService, ActionType, RedirectAction } from '@client-monorepo/common/action-handler';
import { switchMap, takeUntil, timer } from 'rxjs';
import { BaseComponent } from '../../components/core/components/base/base.component';
import { ECreditStatus } from './models/credit-status.enum';
import { InventoryProfitComponent } from './components/inventory-profit/inventory-profit.component';
import { GetCreditGuidsComponent } from './components/get-credit-guids/get-credit-guids.component';
import { GetCreditGuidsBottomSheetComponent } from './components/get-credit-guids-bottomsheet/get-credit-guids-bottom-sheet.component';

@Component({
  selector: 'wealth-applet-wallet',
  standalone: true,
  imports: [
    NgxAppBarComponent,
    InventoryAvailibleComponent,
    WalletActionsComponent,
    WalletDipositHintsComponent,
    WalletCreditComponent,
    SpinnerComponent,
    InventoryProfitComponent,
    GetCreditGuidsComponent,
  ],
  styleUrl: './wallet.component.scss',
  templateUrl: './wallet.component.html',
})
export class WalletComponent extends BaseComponent implements OnInit {
  private router = inject(Router);
  private activatedRouter = inject(ActivatedRoute);
  private walletService = inject(WalletService);
  private routeState = inject(RouteStateService);
  private activatedRoute = inject(ActivatedRoute);
  private bottomSheetService = inject(NgxBottomSheetService);
  private navigationService = inject(WealthNavigationService);
  private actionHandlerService = inject(ActionHandlerService);

  walletId = signal<string | undefined>(undefined);
  walletActions = signal<IWalletActionButton[]>([
    {
      id: 'BTN_DEPOSIT',
      label: 'واریز',
      disable: false,
      style: 'fill',
      icon: {
        name: 'plus',
        type: 'linear',
      },
    },
    {
      id: 'BTN_CASH_OUT',
      label: 'برداشت',
      disable: true,
      style: 'tinted-on-back',
      icon: {
        name: 'arrow-up',
        type: 'linear',
      },
    },
    {
      id: 'BTN_BNPL_REQUEST',
      label: 'درخواست اعتبار',
      disable: false,
      style: 'tinted-on-back',
      icon: {
        name: 'bnpl',
        type: 'linear',
      },
    },
    {
      id: 'BTN_TRANSACTIONS',
      label: 'تراکنش‌ها',
      disable: false,
      style: 'tinted-on-back',
      icon: {
        name: 'card-to-card',
        type: 'linear',
      },
    },
  ]);
  loading = signal<boolean>(true);
  wallet = signal<IWallet | undefined>(undefined);
  state = signal<IProcessData | undefined>(undefined);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.state.set(this.routeState.getAll());
    this.walletId.set(this.activatedRoute.snapshot.params['id']);
    if (this.state().bottomSheetName) {
      this.openBottomSheet(this.state());
    }
    this.getWallet();
  }

  private getWallet() {
    timer(0, 300000)
      .pipe(
        takeUntil(this.destroyObservable),
        switchMap(() => this.walletService.getWalletInfo(this.walletId())),
      )
      .subscribe((res) => {
        if (res.success) {
          this.wallet.set(res.result);
          if (!this.wallet().onBoarded) {
            this.creditGuids();
          }
          this.updateActionButtons();
          this.loading.set(false);
        } else {
          if (res?.error?.code === ErrorCodes.InvalidInstrumentSymbol) {
            this.navigationService.navigate([HOME_ROUTE]);
          }
        }
      });
  }

  actionHandler(id: string) {
    if (id === 'BTN_DEPOSIT') {
      this.walletDeposit();
    } else if (id === 'BTN_CASH_OUT') {
      this.walletWithdrow();
    } else if (id === 'BTN_BNPL_REQUEST') {
      this.handleCredit();
    } else {
      this.supperAppTransaction();
    }
  }

  private updateActionButtons() {
    this.walletActions().find((action) => action.id === 'BTN_CASH_OUT').disable = this.wallet().withdrawalBalance <= 0;
    this.walletActions().find((action) => action.id === 'BTN_BNPL_REQUEST').disable =
      this.wallet().bnpl.status === ECreditStatus.Activated ||
      this.wallet().bnpl.status === ECreditStatus.InProgress ||
      this.wallet().bnpl.status === ECreditStatus.InClosure;
  }

  private walletDeposit() {
    const processData: IWalletProcess = {
      walletName: this.wallet().walletName,
      action: 'start_journey',
      walletId: this.walletId(),
    };
    this.walletService.walletProcess(WALLET_DEPOSIT_PROCESS_API, processData).subscribe();
  }

  private walletWithdrow() {
    const processData: IWalletProcess = {
      walletName: this.wallet().walletName,
      action: 'start_journey',
      walletId: this.walletId(),
      walletTitle: this.wallet().title,
    };
    this.walletService.walletProcess(WALLET_WITHDROW_PROCESS_API, processData).subscribe();
  }

  onBackHandler() {
    const referrer = this.activatedRouter.snapshot.queryParams['referrer'];
    if (referrer === 'wealth') {
      this.navigationService.navigate([HOME_ROUTE]);
    } else {
      this.router.navigateByUrl('/hub');
    }
  }

  handleCredit(action?: ProcessActionType) {
    if (this.wallet().bnpl.status === ECreditStatus.Activated) {
      this.cancelCredit();
    } else {
      this.getBnpl(action);
    }
  }

  private getBnpl(action?: ProcessActionType) {
    const processData: IWalletProcess = {
      walletName: this.wallet().walletName,
      action: action ? action : 'start_journey',
      walletId: this.walletId(),
    };
    this.walletService.walletProcess(WALLET_BNPL_PROCESS_API, processData).subscribe((res) => {
      if (res.success) {
        if (res.result.action === 'bottomSheet') {
          this.openBottomSheet(res.result.data);
        }
      }
    });
  }

  private openBottomSheet(data?: IProcessData) {
    if (data.bottomSheetName === 'page_wallet_bnpl_landing_confirmed') {
      this.bottomSheetService.openBottomSheet(WalletBnplBottomsheetComponent, {
        data,
      });
      const bottomSheetService = this.bottomSheetService.onClose.subscribe(() => {
        bottomSheetService.unsubscribe();
        const result = this.bottomSheetService.outputData();
        if (result && result['confirmed']) {
          this.handleCredit('confirmed');
        }
      });
    } else if (data.bottomSheetName === 'page_wallet_bnpl_invalid_balance') {
      this.bottomSheetService.openBottomSheet(WalletBnplLessAmountBottomsheetComponent, {
        walletId: this.walletId(),
        requiredAmount: data.minAmount,
      });
    }
  }

  cancelCredit() {
    const bnplData: IProcessData = {
      walletName: this.wallet().walletName,
      walletTitle: this.wallet().title,
    };
    this.bottomSheetService.openBottomSheet(WalletBnplCancelBottomsheetComponent, {
      walletName: this.wallet().title,
    });
    this.bottomSheetService.onClose.subscribe(() => {
      const data = this.bottomSheetService.outputData();
      if (data && data.action === 'CONFIRM') {
        this.walletService.walletBnplClose().subscribe((res) => {
          if (res.success) {
            this.navigationService.navigate([WALLET_BNPL_DETAIL_ROUTE, this.wallet().walletName.toLowerCase()], {
              state: {
                cancelResutl: res.result,
                ...bnplData,
              },
            });
          }
        });
      }
    });
  }

  private supperAppTransaction() {
    const action: RedirectAction = {
      type: ActionType.REDIRECT,
      payload: {
        url: 'transactions/report/history',
        state: { customLinkForBack: `mini-app/wealth/wallets/${this.walletId()}?referrer=wealth` },
        params: { type: '72,73' },
      },
    };
    this.actionHandlerService.handle(action);
  }

  creditGuids() {
    this.bottomSheetService.openBottomSheet(GetCreditGuidsBottomSheetComponent, {});
  }
}
